﻿import { mutations } from "@/graphql";
import { useCallback, useState } from "react";
import { useAuth } from "@/app/providers";
const { VITE_API_URL_GRAPHQL } = import.meta.env;

type UpdateUserDocumentPayload = {
  documentId: string;
  file?: File | null;
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  parentId?: string;
  childId?: string;
  tenantId?: string;
  energyType?: string;
  documentType?: string;
};

/**
 * Hook to update a user document via multipart/form-data.
 * Uses mutations.updateUserDocument mutation and builds request with literal values for file upload compatibility.
 * Uses Apollo's configured endpoint from environment variable.
 */
export const useUpdateUserDocument = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { token } = useAuth();

  const updateDocument = useCallback(
    async ({
      documentId,
      file,
      name,
      description,
      startDate,
      endDate,
      parentId,
      childId,
      tenantId,
      energyType,
      documentType,
    }: UpdateUserDocumentPayload) => {
      setLoading(true);
      setError(null);

      try {
        // Escape quotes in string values to prevent GraphQL syntax errors
        const escapedName = (name || "").replace(/"/g, '\\"');
        const escapedDescription = (description || "").replace(/"/g, '\\"');

        // Extract mutation details from mutations.updateUserDocument
        const mutationDef = mutations.updateUserDocument.definitions[0];
        const mutationName = (mutationDef as any).name.value;
        const mutationOperation = (mutationDef as any).selectionSet
          .selections[0];
        const mutationFieldName = mutationOperation.name.value;

        // Extract return fields from the mutation
        const returnFields = mutationOperation.selectionSet.selections
          .map((field: { name: { value: string } }) => field.name.value)
          .join("\n            ");

        // Build mutation with literal values using structure from mutations.updateUserDocument
        const query = `mutation ${mutationName}{
          ${mutationFieldName}(
            documentId: "${documentId}"
            name: "${escapedName}"
            description: "${escapedDescription}"
            startDate: "${startDate}"
            endDate: "${endDate}"
            parentId: "${parentId || ""}"
            childId: "${childId || ""}"
            tenantId: "${tenantId || ""}"
            energyType: "${energyType}"
            documentType: "${documentType}"
          ){
            ${returnFields}
          }
        }`;

        // Build multipart form data with mutation string
        const formData = new FormData();
        formData.append("query", query);

        // Only append file if provided
        if (file) {
          formData.append("file", file, file.name);
        }

        // Use the GraphQL endpoint from environment (same as GraphQLProvider)
        const uri = VITE_API_URL_GRAPHQL;

        // Build headers with authorization token (same as GraphQLProvider)
        const headers = {};
        if (token) {
          (headers as any).authorization = `Bearer ${token}`;
        }

        const response = await fetch(uri, {
          method: "POST",
          headers: headers,
          credentials: "include",
          body: formData,
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0]?.message || "Update failed");
        }

        if (!result.data?.updateUserDocument?.success) {
          const errorMessage = result.data?.updateUserDocument?.message;
          throw new Error(
            Array.isArray(errorMessage)
              ? errorMessage[0]
              : errorMessage || "Update failed",
          );
        }

        setData(result.data);
        setLoading(false);
        return result.data.updateUserDocument;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : String(err) || "Update failed",
        );
        setLoading(false);
        throw err;
      }
    },
    [token],
  );

  return { updateDocument, data, loading, error };
};
